import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_EXPIRES_AT_KEY,
  REFRESH_TOKEN_KEY,
  REFRESH_TOKEN_LIFETIME_MS,
  TOKEN_KEY,
  USER_KEY,
} from "../../utils/authStorage";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  phone?: string;
  joinDate?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: null | AuthUser;
  token: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: AuthUser;
        token: string;
        refreshToken?: string;
      }>,
    ) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      // Persist token under both keys for API compatibility.
      localStorage.setItem(TOKEN_KEY, action.payload.token);
      localStorage.setItem(ACCESS_TOKEN_KEY, action.payload.token);
      localStorage.setItem(USER_KEY, JSON.stringify(action.payload.user));
      if (action.payload.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, action.payload.refreshToken);
        localStorage.setItem(
          REFRESH_TOKEN_EXPIRES_AT_KEY,
          (Date.now() + REFRESH_TOKEN_LIFETIME_MS).toString(),
        );
      }
    },
    updateToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem(TOKEN_KEY, action.payload);
      localStorage.setItem(ACCESS_TOKEN_KEY, action.payload);
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      // Clear token from localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
      localStorage.removeItem(USER_KEY);
    },
    restoreSession: (
      state,
      action: PayloadAction<{
        user: AuthUser;
        token: string;
      }>,
    ) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
  },
});

export const { loginSuccess, updateToken, logout, restoreSession } =
  authSlice.actions;
export default authSlice.reducer;
